import { environment } from '../environments/environment';
import { AuthData, useAuthStore } from '../store/authStore';

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  session_id: string;
}

export async function login(auth: AuthData): Promise<LoginResponse> {
  const response = await fetch(`${environment.API_BASE_URL}/api/v1/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      channel: auth.platform,
      region: auth.region,
      user_type: auth.user_type,
      token: auth.token,
    }),
  });

  // Login only succeeds on HTTP 200 with both access_token and session_id present.
  if (response.status !== 200) {
    throw new Error(`Auth request failed with status ${response.status}`);
  }

  const data = await response.json().catch(() => null);

  if (!data?.access_token || !data?.session_id) {
    throw new Error('Auth response missing access_token or session_id');
  }

  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    session_id: data.session_id,
  };
}

// Exchanges the refresh token for a fresh access token. Any non-200 response
// (or a payload missing the new access token) is treated as an auth failure.
async function refresh(refreshToken: string): Promise<LoginResponse> {
  const response = await fetch(
    `${environment.API_BASE_URL}/api/v1/auth/refresh`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }
  );

  if (response.status !== 200) {
    throw new Error(`Token refresh failed with status ${response.status}`);
  }

  const data = await response.json().catch(() => null);
  if (!data?.access_token || !data?.session_id) {
    throw new Error('Refresh response missing access_token or session_id');
  }

  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    session_id: data.session_id,
  };
}

// Shared in-flight refresh so concurrent 401s (e.g. send + history) trigger a
// single refresh request rather than racing each other.
let refreshPromise: Promise<string> | null = null;

// Refreshes the access token using the stored refresh token, updates the auth
// store with the new tokens, and resolves with the new access token. On
// failure it flips authStatus to 'error' so the app shows the auth-fail screen.
export function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const {
        refreshToken,
        setAiToken,
        setRefreshToken,
        setSessionId,
        setAuthStatus,
      } = useAuthStore.getState();

      if (!refreshToken) {
        setAuthStatus('error');
        throw new Error('No refresh token available');
      }

      try {
        const data = await refresh(refreshToken);
        setAiToken(data.access_token);
        setRefreshToken(data.refresh_token);
        setSessionId(data.session_id);
        return data.access_token;
      } catch (err) {
        setAuthStatus('error');
        throw err;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}
